/**
 * ComplianceTab
 *
 * Collapsible sections: Licenses, Overtime & Admin, Note Quality.
 * Each section has filters (including search) and tables.
 */

import {
  Alert,
  Badge,
  Box,
  Flex,
  Group,
  Loader,
  Paper,
  Slider,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import type { TitleAccordionItem } from '../../../components/TitleAccordion';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { SearchInput } from '../../../components/SearchInput';
import { TitleAccordion } from '../../../components/TitleAccordion';
import { DateInput } from '@mantine/dates';
import { dateInputValueToDateOnly, parseDateOnlyLocal } from '@/lib/dateHelpers';
import type { ComponentProps } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TablePaginationFooter } from '@/components/TablePaginationFooter';
import { useComplianceFromMedplum } from '../hooks/useComplianceFromMedplum';
import type {
  LicenseFilters as LicenseFiltersType,
  LicenseRow,
  NoteQualityRow,
  OvertimeAdminFilters as OvertimeAdminFiltersType,
  QualityFilters as QualityFiltersType,
} from '../types';
import type { NoteQualityWarningType } from '../types';
import {
  defaultLicenseFilters,
  defaultOvertimeAdminFilters,
  defaultQualityFilters,
  LicenseStatusLabels,
  NoteQualityWarningTypeLabels,
} from '../types';

/** Default color cycle for warning-type badges (matches StylingPage "Tag Colors / Rotating Palette"). */
const NOTE_QUALITY_WARNING_TYPE_COLORS: Record<NoteQualityWarningType, string> = {
  'edit-distance': 'yellow',
  'adjustment-disorder': 'green',
};

const licenseStatusOptions = Object.entries(LicenseStatusLabels).map(([value, label]) => ({ value, label }));

function formatDate(d: string) {
  return d ? new Date(d).toLocaleDateString('en-US') : '—';
}

export function ComplianceTab(): JSX.Element {
  const {
    licenses: liveLicenses,
    weeklyHours: liveWeeklyHours,
    adminOverages: liveAdminOverages,
    dailyHours: liveDailyHours,
    monthsFullTime: liveMonthsFullTime,
    monthlyHours: liveMonthlyHours,
    noteQuality: liveNoteQuality,
    providerOptions,
    loading: complianceLoading,
    error: complianceError,
  } = useComplianceFromMedplum();

  const [licenseFilters, setLicenseFilters] = useState<LicenseFiltersType>(defaultLicenseFilters);
  const [overtimeFilters, setOvertimeFilters] = useState<OvertimeAdminFiltersType>(defaultOvertimeAdminFilters);
  const [qualityFilters, setQualityFilters] = useState<QualityFiltersType>(defaultQualityFilters);
  const warningTypeOptions = useMemo(
    () => Object.entries(NoteQualityWarningTypeLabels).map(([value, label]) => ({ value, label })),
    []
  );

  const filteredLicenses = useMemo(() => {
    let list = liveLicenses;
    const q = licenseFilters.search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          i.providerName.toLowerCase().includes(q) ||
          i.highestLicense.toLowerCase().includes(q)
      );
    }
    if (licenseFilters.status.length > 0) {
      list = list.filter((i) => licenseFilters.status.includes(i.status));
    }
    return list;
  }, [liveLicenses, licenseFilters.search, licenseFilters.status]);

  type LicenseSortBy = 'provider' | 'licenseExpiration';
  const [licenseSortBy, setLicenseSortBy] = useState<LicenseSortBy>('licenseExpiration');
  const [licenseSortDir, setLicenseSortDir] = useState<'asc' | 'desc'>('asc');
  const sortedLicenses = useMemo(() => {
    const mult = licenseSortDir === 'asc' ? 1 : -1;
    return [...filteredLicenses].sort((a, b) => {
      if (licenseSortBy === 'provider') return mult * (a.providerName.localeCompare(b.providerName));
      return mult * (new Date(a.licenseExpiration).getTime() - new Date(b.licenseExpiration).getTime());
    });
  }, [filteredLicenses, licenseSortBy, licenseSortDir]);

  const handleLicenseSort = useCallback((col: LicenseSortBy) => {
    setLicenseSortBy((prev) => {
      if (prev === col) setLicenseSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else setLicenseSortDir('asc');
      return col;
    });
  }, []);
  const NBSP = '\u00A0';
  const licenseSortIndicator = (col: LicenseSortBy) =>
    licenseSortBy === col ? (licenseSortDir === 'asc' ? `${NBSP}↑` : `${NBSP}↓`) : '';

  const filteredWeekly = useMemo(
    () => filterOvertimeTables(liveWeeklyHours, overtimeFilters, (row) => row.weekStart),
    [liveWeeklyHours, overtimeFilters]
  );
  const filteredAdminOverages = useMemo(
    () => filterOvertimeTables(liveAdminOverages, overtimeFilters, (row) => row.weekStart),
    [liveAdminOverages, overtimeFilters]
  );
  const filteredDaily = useMemo(
    () => filterOvertimeTables(liveDailyHours, overtimeFilters, (row) => row.date),
    [liveDailyHours, overtimeFilters]
  );
  const filteredMonthsFullTime = useMemo(
    () => filterOvertimeBySearchAndProvider(liveMonthsFullTime, overtimeFilters),
    [liveMonthsFullTime, overtimeFilters]
  );
  const filteredMonthly = useMemo(
    () => filterOvertimeTables(liveMonthlyHours, overtimeFilters, (row) => row.monthStart),
    [liveMonthlyHours, overtimeFilters]
  );

  type SortDir = 'asc' | 'desc';
  const sortDir = (col: string, current: string, dir: SortDir) => (current === col ? (dir === 'asc' ? `${NBSP}↑` : `${NBSP}↓`) : '');
  const [weeklySort, setWeeklySort] = useState<{ by: 'provider' | 'weekStart' | 'overtimeHours'; dir: SortDir }>({ by: 'weekStart', dir: 'asc' });
  const [adminSort, setAdminSort] = useState<{ by: 'provider' | 'weekStart' | 'adminRatio' | 'adminHours' | 'clinicalHours'; dir: SortDir }>({ by: 'weekStart', dir: 'asc' });
  const [dailySort, setDailySort] = useState<{ by: 'provider' | 'date' | 'totalHours' | 'adminHours' | 'overtimeHours'; dir: SortDir }>({ by: 'date', dir: 'asc' });
  const [monthsFullTimeSort, setMonthsFullTimeSort] = useState<{ by: 'provider' | 'monthsOver130Hours'; dir: SortDir }>({ by: 'provider', dir: 'asc' });
  const [monthlySort, setMonthlySort] = useState<{ by: 'provider' | 'monthStart' | 'totalHours'; dir: SortDir }>({ by: 'monthStart', dir: 'asc' });

  const sortedWeekly = useMemo(() => {
    const mult = weeklySort.dir === 'asc' ? 1 : -1;
    return [...filteredWeekly].sort((a, b) => {
      if (weeklySort.by === 'provider') return mult * a.providerName.localeCompare(b.providerName);
      if (weeklySort.by === 'weekStart') return mult * (new Date(a.weekStart).getTime() - new Date(b.weekStart).getTime());
      return mult * (a.overtimeHours - b.overtimeHours);
    });
  }, [filteredWeekly, weeklySort.by, weeklySort.dir]);
  const sortedAdminOverages = useMemo(() => {
    const mult = adminSort.dir === 'asc' ? 1 : -1;
    return [...filteredAdminOverages].sort((a, b) => {
      if (adminSort.by === 'provider') return mult * a.providerName.localeCompare(b.providerName);
      if (adminSort.by === 'weekStart') return mult * (new Date(a.weekStart).getTime() - new Date(b.weekStart).getTime());
      if (adminSort.by === 'adminRatio') return mult * (a.adminRatio - b.adminRatio);
      if (adminSort.by === 'adminHours') return mult * (a.adminHours - b.adminHours);
      return mult * (a.clinicalHours - b.clinicalHours);
    });
  }, [filteredAdminOverages, adminSort.by, adminSort.dir]);
  const sortedDaily = useMemo(() => {
    const mult = dailySort.dir === 'asc' ? 1 : -1;
    return [...filteredDaily].sort((a, b) => {
      if (dailySort.by === 'provider') return mult * a.providerName.localeCompare(b.providerName);
      if (dailySort.by === 'date') return mult * (new Date(a.date).getTime() - new Date(b.date).getTime());
      if (dailySort.by === 'totalHours') return mult * (a.totalHours - b.totalHours);
      if (dailySort.by === 'adminHours') return mult * (a.adminHours - b.adminHours);
      return mult * (a.overtimeHours - b.overtimeHours);
    });
  }, [filteredDaily, dailySort.by, dailySort.dir]);
  const sortedMonthsFullTime = useMemo(() => {
    const mult = monthsFullTimeSort.dir === 'asc' ? 1 : -1;
    return [...filteredMonthsFullTime].sort((a, b) => {
      if (monthsFullTimeSort.by === 'provider') return mult * a.providerName.localeCompare(b.providerName);
      return mult * (a.monthsOver130Hours - b.monthsOver130Hours);
    });
  }, [filteredMonthsFullTime, monthsFullTimeSort.by, monthsFullTimeSort.dir]);
  const sortedMonthly = useMemo(() => {
    const mult = monthlySort.dir === 'asc' ? 1 : -1;
    return [...filteredMonthly].sort((a, b) => {
      if (monthlySort.by === 'provider') return mult * a.providerName.localeCompare(b.providerName);
      if (monthlySort.by === 'monthStart') return mult * (new Date(a.monthStart).getTime() - new Date(b.monthStart).getTime());
      return mult * (a.totalHours - b.totalHours);
    });
  }, [filteredMonthly, monthlySort.by, monthlySort.dir]);

  const filteredNoteQuality = useMemo(() => {
    let list = liveNoteQuality;
    const q = qualityFilters.search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          i.providerName.toLowerCase().includes(q) ||
          (i.clientName ?? '').toLowerCase().includes(q)
      );
    }
    if (qualityFilters.providerIds.length > 0) {
      list = list.filter((i) => qualityFilters.providerIds.includes(i.providerId));
    }
    if (qualityFilters.levenshteinMax < 1000) {
      list = list.filter(
        (i) => i.warningType !== 'edit-distance' || (i.editDistance != null && i.editDistance <= qualityFilters.levenshteinMax)
      );
    }
    if (qualityFilters.warningTypes.length > 0) {
      list = list.filter((i) => qualityFilters.warningTypes.includes(i.warningType));
    }
    return list;
  }, [liveNoteQuality, qualityFilters.search, qualityFilters.providerIds, qualityFilters.levenshteinMax, qualityFilters.warningTypes]);

  const [licensePage, setLicensePage] = useState(1);
  const [licenseRpp, setLicenseRpp] = useState('50');
  const [weeklyPage, setWeeklyPage] = useState(1);
  const [weeklyRpp, setWeeklyRpp] = useState('50');
  const [adminOveragesPage, setAdminOveragesPage] = useState(1);
  const [adminOveragesRpp, setAdminOveragesRpp] = useState('50');
  const [dailyPage, setDailyPage] = useState(1);
  const [dailyRpp, setDailyRpp] = useState('50');
  const [monthsFullTimePage, setMonthsFullTimePage] = useState(1);
  const [monthsFullTimeRpp, setMonthsFullTimeRpp] = useState('50');
  const [monthlyPage, setMonthlyPage] = useState(1);
  const [monthlyRpp, setMonthlyRpp] = useState('50');
  const [noteQualityPage, setNoteQualityPage] = useState(1);
  const [noteQualityRpp, setNoteQualityRpp] = useState('50');

  useEffect(() => { setLicensePage(1); }, [sortedLicenses.length]);
  useEffect(() => { setWeeklyPage(1); }, [sortedWeekly.length]);
  useEffect(() => { setAdminOveragesPage(1); }, [sortedAdminOverages.length]);
  useEffect(() => { setDailyPage(1); }, [sortedDaily.length]);
  useEffect(() => { setMonthsFullTimePage(1); }, [sortedMonthsFullTime.length]);
  useEffect(() => { setMonthlyPage(1); }, [sortedMonthly.length]);
  useEffect(() => { setNoteQualityPage(1); }, [filteredNoteQuality.length]);

  const licenseIpp = parseInt(licenseRpp || '50', 10);
  const paginatedLicenses = useMemo(
    () => sortedLicenses.slice((licensePage - 1) * licenseIpp, licensePage * licenseIpp),
    [sortedLicenses, licensePage, licenseIpp]
  );
  const weeklyIpp = parseInt(weeklyRpp || '50', 10);
  const paginatedWeekly = useMemo(
    () => sortedWeekly.slice((weeklyPage - 1) * weeklyIpp, weeklyPage * weeklyIpp),
    [sortedWeekly, weeklyPage, weeklyIpp]
  );
  const adminOveragesIpp = parseInt(adminOveragesRpp || '50', 10);
  const paginatedAdminOverages = useMemo(
    () => sortedAdminOverages.slice((adminOveragesPage - 1) * adminOveragesIpp, adminOveragesPage * adminOveragesIpp),
    [sortedAdminOverages, adminOveragesPage, adminOveragesIpp]
  );
  const dailyIpp = parseInt(dailyRpp || '50', 10);
  const paginatedDaily = useMemo(
    () => sortedDaily.slice((dailyPage - 1) * dailyIpp, dailyPage * dailyIpp),
    [sortedDaily, dailyPage, dailyIpp]
  );
  const monthsFullTimeIpp = parseInt(monthsFullTimeRpp || '50', 10);
  const paginatedMonthsFullTime = useMemo(
    () => sortedMonthsFullTime.slice((monthsFullTimePage - 1) * monthsFullTimeIpp, monthsFullTimePage * monthsFullTimeIpp),
    [sortedMonthsFullTime, monthsFullTimePage, monthsFullTimeIpp]
  );
  const monthlyIpp = parseInt(monthlyRpp || '50', 10);
  const paginatedMonthly = useMemo(
    () => sortedMonthly.slice((monthlyPage - 1) * monthlyIpp, monthlyPage * monthlyIpp),
    [sortedMonthly, monthlyPage, monthlyIpp]
  );
  const noteQualityIpp = parseInt(noteQualityRpp || '50', 10);
  const paginatedNoteQuality = useMemo(
    () => filteredNoteQuality.slice((noteQualityPage - 1) * noteQualityIpp, noteQualityPage * noteQualityIpp),
    [filteredNoteQuality, noteQualityPage, noteQualityIpp]
  );

  const accordionItems: TitleAccordionItem[] = [
    {
      value: 'licenses',
      label: 'Licenses',
      children: (
        <Stack gap="md" p="0">
          <Group gap="md" wrap="wrap" align="center">
            <SearchInput
              placeholder="Search..."
              iconSize={14}
              value={licenseFilters.search}
              onChange={(e) => setLicenseFilters((f) => ({ ...f, search: e.currentTarget.value }))}
              w={280}
            />
            <FilterMultiSelect
              allLabel="All statuses"
              data={licenseStatusOptions}
              value={licenseFilters.status}
              onChange={(v) => setLicenseFilters((f) => ({ ...f, status: v as LicenseFiltersType['status'] }))}
              w={220}
            />
          </Group>
          <Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
            <div style={{ overflow: 'auto', minHeight: 200, maxHeight: 'min(420px, 55vh)' }}>
              <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 600 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleLicenseSort('provider')}>
                      Provider{licenseSortIndicator('provider')}
                    </Table.Th>
                    <Table.Th>Highest License</Table.Th>
                    <Table.Th style={{ cursor: 'pointer' }} onClick={() => handleLicenseSort('licenseExpiration')}>
                      License Expiration{licenseSortIndicator('licenseExpiration')}
                    </Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredLicenses.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text c="gray.6" size="sm" ta="center">
                          No licenses match the filters.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    paginatedLicenses.map((row: LicenseRow) => (
                      <Table.Tr key={row.id}>
                        <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                        <Table.Td><Text size="sm">{row.highestLicense}</Text></Table.Td>
                        <Table.Td><Text size="sm">{formatDate(row.licenseExpiration)}</Text></Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            variant="light"
                            color={row.status === 'expired' ? 'red' : row.status === 'expiring-soon' ? 'yellow' : 'green'}
                          >
                            {LicenseStatusLabels[row.status]}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </div>
            <TablePaginationFooter
              totalItems={sortedLicenses.length}
              currentPage={licensePage}
              onPageChange={setLicensePage}
              rowsPerPage={licenseRpp}
              onRowsPerPageChange={(v) => setLicenseRpp(v ?? '50')}
            />
          </Paper>
        </Stack>
      ),
    },
    {
      value: 'overtime-admin',
      label: 'Overtime & Admin',
      children: (
        <Stack gap="md" p="0">
          <Group gap="md" wrap="wrap" align="flex-end">
            <SearchInput
              placeholder="Search..."
              iconSize={14}
              value={overtimeFilters.search}
              onChange={(e) => setOvertimeFilters((f) => ({ ...f, search: e.currentTarget.value }))}
              w={280}
            />
            <DateInput
              placeholder="Date range start"
              value={overtimeFilters.dateRangeStart ? parseDateOnlyLocal(overtimeFilters.dateRangeStart) : null}
              valueFormat="MM/DD/YYYY"
              onChange={(d) =>
                setOvertimeFilters((f) => ({
                  ...f,
                  dateRangeStart: d ? dateInputValueToDateOnly(d) : '',
                }))
              }
              style={{ minWidth: 160 }}
              clearable
            />
            <DateInput
              placeholder="Date range end"
              value={overtimeFilters.dateRangeEnd ? parseDateOnlyLocal(overtimeFilters.dateRangeEnd) : null}
              valueFormat="MM/DD/YYYY"
              onChange={(d) =>
                setOvertimeFilters((f) => ({
                  ...f,
                  dateRangeEnd: d ? dateInputValueToDateOnly(d) : '',
                }))
              }
              style={{ minWidth: 160 }}
              clearable
            />
            <FilterMultiSelect
              allLabel="All providers"
              data={providerOptions}
              value={overtimeFilters.providerIds}
              onChange={(v) => setOvertimeFilters((f) => ({ ...f, providerIds: v }))}
              w={220}
            />
          </Group>

          <Flex gap="md" wrap="wrap" align="stretch">
            <Box style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column' }}>
              <Text size="sm" fw={600} mb="xs">Weekly Hour Totals</Text>
              <Paper p="lg" withBorder style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ overflow: 'auto', flex: 1, minHeight: 0, width: '100%' }}>
                  <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 320 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setWeeklySort((p) => ({ by: 'provider', dir: p.by === 'provider' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Provider{sortDir('provider', weeklySort.by, weeklySort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setWeeklySort((p) => ({ by: 'weekStart', dir: p.by === 'weekStart' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Week Start{sortDir('weekStart', weeklySort.by, weeklySort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setWeeklySort((p) => ({ by: 'overtimeHours', dir: p.by === 'overtimeHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Overtime Hours{sortDir('overtimeHours', weeklySort.by, weeklySort.dir)}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedWeekly.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                          <Table.Td><Text size="sm">{formatDate(row.weekStart)}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.overtimeHours}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
                <TablePaginationFooter
                  totalItems={sortedWeekly.length}
                  currentPage={weeklyPage}
                  onPageChange={setWeeklyPage}
                  rowsPerPage={weeklyRpp}
                  onRowsPerPageChange={(v) => setWeeklyRpp(v ?? '50')}
                />
              </Paper>
            </Box>
            <Box style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column' }}>
              <Text size="sm" fw={600} mb="xs">Admin Hour Overages</Text>
              <Paper p="lg" withBorder style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ overflow: 'auto', flex: 1, minHeight: 0, width: '100%' }}>
                  <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 400 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setAdminSort((p) => ({ by: 'provider', dir: p.by === 'provider' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Provider{sortDir('provider', adminSort.by, adminSort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setAdminSort((p) => ({ by: 'weekStart', dir: p.by === 'weekStart' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Week Start{sortDir('weekStart', adminSort.by, adminSort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setAdminSort((p) => ({ by: 'adminRatio', dir: p.by === 'adminRatio' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Admin Ratio{sortDir('adminRatio', adminSort.by, adminSort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setAdminSort((p) => ({ by: 'adminHours', dir: p.by === 'adminHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Admin{sortDir('adminHours', adminSort.by, adminSort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setAdminSort((p) => ({ by: 'clinicalHours', dir: p.by === 'clinicalHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Clinical{sortDir('clinicalHours', adminSort.by, adminSort.dir)}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedAdminOverages.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                          <Table.Td><Text size="sm">{formatDate(row.weekStart)}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.adminRatio.toFixed(2)}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.adminHours}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.clinicalHours}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
                <TablePaginationFooter
                  totalItems={sortedAdminOverages.length}
                  currentPage={adminOveragesPage}
                  onPageChange={setAdminOveragesPage}
                  rowsPerPage={adminOveragesRpp}
                  onRowsPerPageChange={(v) => setAdminOveragesRpp(v ?? '50')}
                />
              </Paper>
            </Box>
          </Flex>

          <div>
            <Text size="sm" fw={600} mb="xs">Daily Hour Totals</Text>
            <Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
              <div style={{ overflow: 'auto', minHeight: 200, maxHeight: 'min(420px, 55vh)' }}>
                <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 500 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ cursor: 'pointer' }} onClick={() => setDailySort((p) => ({ by: 'provider', dir: p.by === 'provider' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Provider{sortDir('provider', dailySort.by, dailySort.dir)}</Table.Th>
                      <Table.Th style={{ cursor: 'pointer' }} onClick={() => setDailySort((p) => ({ by: 'date', dir: p.by === 'date' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Date{sortDir('date', dailySort.by, dailySort.dir)}</Table.Th>
                      <Table.Th style={{ cursor: 'pointer' }} onClick={() => setDailySort((p) => ({ by: 'totalHours', dir: p.by === 'totalHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Total Hours{sortDir('totalHours', dailySort.by, dailySort.dir)}</Table.Th>
                      <Table.Th style={{ cursor: 'pointer' }} onClick={() => setDailySort((p) => ({ by: 'adminHours', dir: p.by === 'adminHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Admin Hours{sortDir('adminHours', dailySort.by, dailySort.dir)}</Table.Th>
                      <Table.Th style={{ cursor: 'pointer' }} onClick={() => setDailySort((p) => ({ by: 'overtimeHours', dir: p.by === 'overtimeHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Overtime Hours{sortDir('overtimeHours', dailySort.by, dailySort.dir)}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedDaily.map((row) => (
                      <Table.Tr key={row.id}>
                        <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                        <Table.Td><Text size="sm">{formatDate(row.date)}</Text></Table.Td>
                        <Table.Td><Text size="sm">{row.totalHours}</Text></Table.Td>
                        <Table.Td><Text size="sm">{row.adminHours}</Text></Table.Td>
                        <Table.Td><Text size="sm">{row.overtimeHours}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
              <TablePaginationFooter
                totalItems={sortedDaily.length}
                currentPage={dailyPage}
                onPageChange={setDailyPage}
                rowsPerPage={dailyRpp}
                onRowsPerPageChange={(v) => setDailyRpp(v ?? '50')}
              />
            </Paper>
          </div>

          <Flex gap="md" wrap="wrap" align="stretch">
            <Box style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column' }}>
              <Text size="sm" fw={600} mb="xs">Months Full Time</Text>
              <Paper p="lg" withBorder style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ overflow: 'auto', flex: 1, minHeight: 0, width: '100%' }}>
                  <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 280 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setMonthsFullTimeSort((p) => ({ by: 'provider', dir: p.by === 'provider' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Provider{sortDir('provider', monthsFullTimeSort.by, monthsFullTimeSort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setMonthsFullTimeSort((p) => ({ by: 'monthsOver130Hours', dir: p.by === 'monthsOver130Hours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Months &gt; 130 Hours{sortDir('monthsOver130Hours', monthsFullTimeSort.by, monthsFullTimeSort.dir)}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedMonthsFullTime.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.monthsOver130Hours}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
                <TablePaginationFooter
                  totalItems={sortedMonthsFullTime.length}
                  currentPage={monthsFullTimePage}
                  onPageChange={setMonthsFullTimePage}
                  rowsPerPage={monthsFullTimeRpp}
                  onRowsPerPageChange={(v) => setMonthsFullTimeRpp(v ?? '50')}
                />
              </Paper>
            </Box>
            <Box style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column' }}>
              <Text size="sm" fw={600} mb="xs">Monthly Hour Totals</Text>
              <Paper p="lg" withBorder style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ overflow: 'auto', flex: 1, minHeight: 0, width: '100%' }}>
                  <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 320 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setMonthlySort((p) => ({ by: 'provider', dir: p.by === 'provider' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Provider{sortDir('provider', monthlySort.by, monthlySort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setMonthlySort((p) => ({ by: 'monthStart', dir: p.by === 'monthStart' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Month Start{sortDir('monthStart', monthlySort.by, monthlySort.dir)}</Table.Th>
                        <Table.Th style={{ cursor: 'pointer' }} onClick={() => setMonthlySort((p) => ({ by: 'totalHours', dir: p.by === 'totalHours' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}>Total Hours{sortDir('totalHours', monthlySort.by, monthlySort.dir)}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedMonthly.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                          <Table.Td><Text size="sm">{formatDate(row.monthStart)}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.totalHours}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
                <TablePaginationFooter
                  totalItems={sortedMonthly.length}
                  currentPage={monthlyPage}
                  onPageChange={setMonthlyPage}
                  rowsPerPage={monthlyRpp}
                  onRowsPerPageChange={(v) => setMonthlyRpp(v ?? '50')}
                />
              </Paper>
            </Box>
          </Flex>
        </Stack>
      ),
    },
    {
      value: 'quality',
      label: 'Note Quality',
      children: (
        <Stack gap="md" p="0">
          <Group gap="md" wrap="wrap" align="flex-end">
            <SearchInput
              placeholder="Search..."
              iconSize={14}
              value={qualityFilters.search}
              onChange={(e) => setQualityFilters((f) => ({ ...f, search: e.currentTarget.value }))}
              w={280}
            />
            <FilterMultiSelect
              allLabel="All providers"
              data={providerOptions}
              value={qualityFilters.providerIds}
              onChange={(v) => setQualityFilters((f) => ({ ...f, providerIds: v }))}
              w={220}
            />
            <FilterMultiSelect
              allLabel="All warning types"
              data={warningTypeOptions}
              value={qualityFilters.warningTypes}
              onChange={(v) => setQualityFilters((f) => ({ ...f, warningTypes: v as QualityFiltersType['warningTypes'] }))}
              w={220}
            />
          </Group>
          <div style={{ width: '100%' }}>
            <Text size="sm" fw={500} mb={4}>Levenshtein distance (show notes with distance ≤): {qualityFilters.levenshteinMax}</Text>
            <Slider
              min={0}
              max={1000}
              value={qualityFilters.levenshteinMax}
              onChange={(v) => setQualityFilters((f) => ({ ...f, levenshteinMax: v }))}
              label={(v) => v}
              marks={[{ value: 0, label: '0' }, { value: 250, label: '250' }, { value: 500, label: '500' }, { value: 750, label: '750' }, { value: 1000, label: '1000' }]}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <Text size="sm" fw={600} mb="xs">Note Quality</Text>
            <Text size="xs" c="dimmed" mb="xs">
              Notes with Levenshtein distance filter. Warning types: Edit distance (similarity to prior note), Adjustment disorder (duration &gt; 90 days).
            </Text>
            <Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
              <div style={{ overflow: 'auto', minHeight: 200, maxHeight: 'min(420px, 55vh)' }}>
                <Table highlightOnHover style={{ width: '100%', tableLayout: 'fixed', minWidth: 600 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Provider</Table.Th>
                      <Table.Th>Client</Table.Th>
                      <Table.Th>Session Date</Table.Th>
                      <Table.Th>Warning Type</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredNoteQuality.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Text c="gray.6" size="sm" ta="center">
                            No note quality items match the filters.
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      paginatedNoteQuality.map((row: NoteQualityRow) => (
                        <Table.Tr key={row.id}>
                          <Table.Td><Text size="sm">{row.providerName}</Text></Table.Td>
                          <Table.Td><Text size="sm">{row.clientName}</Text></Table.Td>
                          <Table.Td><Text size="sm">{formatDate(row.sessionDate)}</Text></Table.Td>
                          <Table.Td>
                            <Badge size="sm" variant="light" color={NOTE_QUALITY_WARNING_TYPE_COLORS[row.warningType]}>
                              {NoteQualityWarningTypeLabels[row.warningType]}
                              {row.editDistance != null ? ` (${row.editDistance})` : ''}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </div>
              <TablePaginationFooter
                totalItems={filteredNoteQuality.length}
                currentPage={noteQualityPage}
                onPageChange={setNoteQualityPage}
                rowsPerPage={noteQualityRpp}
                onRowsPerPageChange={(v) => setNoteQualityRpp(v ?? '50')}
              />
            </Paper>
          </div>
        </Stack>
      ),
    },
  ];

  const accordionMultipleProps = {
    multiple: true,
    defaultValue: ['licenses'],
  } as unknown as Partial<ComponentProps<typeof TitleAccordion>>;

  if (complianceLoading) {
    return (
      <Stack gap="md">
        <Group gap="md">
          <Loader size="sm" />
          <span>Loading compliance data…</span>
        </Group>
      </Stack>
    );
  }

  if (complianceError) {
    return (
      <Stack gap="md">
        <Alert color="red" title="Error loading compliance data">
          {complianceError}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" style={{ width: '100%' }}>
      <Alert color="yellow" title="Work in Progress" variant="light">
        This compliance section is under active development. Data shown here is for preview purposes only and may not reflect the final design.
      </Alert>
      <TitleAccordion
        variant="separated"
        items={accordionItems}
        {...accordionMultipleProps}
      />
    </Stack>
  );
}

function filterOvertimeBySearchAndProvider<T extends { providerId: string; providerName: string }>(
  list: T[],
  filters: OvertimeAdminFiltersType
): T[] {
  let out = list;
  const q = filters.search.trim().toLowerCase();
  if (q) out = out.filter((i) => i.providerName.toLowerCase().includes(q));
  if (filters.providerIds.length > 0) out = out.filter((i) => filters.providerIds.includes(i.providerId));
  return out;
}

function filterOvertimeTables<T extends { providerId: string; providerName: string }>(
  list: T[],
  filters: OvertimeAdminFiltersType,
  getDate: (row: T) => string
): T[] {
  let out = filterOvertimeBySearchAndProvider(list, filters);
  if (filters.dateRangeStart || filters.dateRangeEnd) {
    const start = filters.dateRangeStart ? new Date(filters.dateRangeStart).getTime() : 0;
    const end = filters.dateRangeEnd ? new Date(filters.dateRangeEnd).getTime() : Number.MAX_SAFE_INTEGER;
    out = out.filter((row) => {
      const d = getDate(row);
      if (!d) return true;
      const t = new Date(d).getTime();
      return t >= start && t <= end;
    });
  }
  return out;
}
